/**
 * Splits a line the assistant wrote into pieces a template can draw as links.
 *
 * The assistant answers in prose and puts real addresses in it — the guest link a new
 * offer was issued under, the address an offer was posted to, now and then a screen in
 * this very portal. All of it was TEXT: the desk read
 * "Izvedeno — ustvarjena ponudba … · https://guest.pms.imprion.dev/ponudba/SeUer…" and
 * then selected forty characters with a mouse to give a guest the link.
 *
 * # Why a splitter and not a renderer
 *
 * Nothing here produces markup and nothing here is ever handed to `v-html`. It returns
 * DATA — a list of segments — and the template decides what a segment is drawn with.
 * The text being split is written by a language model over the hotel's own data, which
 * is exactly the input that must never be able to bring an element with it. A helper
 * that returned a string of HTML would put that one decision in a place where the next
 * person to touch it cannot see the risk.
 *
 * # What counts as a link
 *
 *   - `http://` and `https://` addresses, ended by whitespace or by the punctuation a
 *     sentence ends with — see `trimTrailing` for why a bracket is not simply stripped.
 *   - `mailto:` and a bare e-mail address, both of which become a `mailto:` link.
 *   - a path into this portal, when it starts with one of `INTERNAL_PREFIXES` — which
 *     in THIS portal is empty, so nothing is ever drawn as an internal link here. See
 *     the constant for why.
 *   - a MARKDOWN link, `[label](https://…)` — the shape the model now writes an address
 *     in — which produces ONE segment of whichever kind its target is. The label is what
 *     is drawn, whole and never shortened: it is prose the model wrote for this reader,
 *     and `shortenUrl` exists to cut a query string, not a sentence. Its target could be
 *     an internal path in the reception portal; here it cannot, for the same reason as
 *     above — the prefix list is empty, so a Markdown link to a path is left as text.
 *
 *     Nothing else in Markdown is understood. `**bold**` is left exactly as written:
 *     this file splits addresses and is not a Markdown renderer, and the day it becomes
 *     one is the day something here starts producing markup.
 *
 * # Worked examples — there is no test runner in this portal
 *
 * `pms-web` has no approved test runner (CLAUDE.md deviation 10 is the same story for
 * translations), so the cases this was written against are written down instead. Each
 * one is a thing that actually goes wrong when the splitting is naive:
 *
 *   1. An address at the end of a sentence
 *      `"Ponudba je na https://guest.pms.imprion.dev/ponudba/SeUer."`
 *      → text, external(url `…/SeUer`, label `guest.pms.imprion.dev/ponudba/SeUer`),
 *        text `"."` — the full stop belongs to the sentence, not to the token.
 *
 *   2. Two addresses on one line
 *      `"Stara https://a.example/x, nova https://b.example/y"`
 *      → external, text `", nova "`, external. The comma ends the first one; the walk
 *        does not stop at the first match.
 *
 *   3. An e-mail, bare
 *      `"poslano na info@hotel.si"` → text, mail(url `mailto:info@hotel.si`,
 *        label `info@hotel.si`).
 *
 *   4. A bracketed address
 *      `"(https://a.example/x)"` → text `"("`, external, text `")"`, but
 *      `"https://en.wikipedia.org/wiki/Hotel_(disambiguation)"` keeps its bracket,
 *      because the address opened one itself.
 *
 *   5. An address inside an address
 *      `"https://a.example/?to=info@hotel.si"` is ONE external segment — the e-mail
 *      inside it is not pulled out, because the URL match starts earlier and wins.
 *
 *   6. A path is never a link here
 *      `"/rezervacije/42"` stays text, because no prefix matches it, and so does
 *      `"/sheet"`. The glue rule below (a path is only a path at the start of a line or
 *      after a space) is kept as the reception portal has it, so that the two files stay
 *      one file apart.
 *
 *   7. A long address is shortened for reading and never for following
 *      `"https://guest.pms.imprion.dev/ponudba/SeUerXyz123456"`
 *      → label `guest.pms.imprion.dev/ponudba/SeUer…`, url the whole thing. The
 *        component puts the whole thing in `title` and on the clipboard.
 *
 * And the Markdown cases, which is the table that branch was written against. A segment
 * is written `kind(text → url)`:
 *
 *   | line                                      | segments                               |
 *   | ----------------------------------------- | -------------------------------------- |
 *   | `Pot: [Pot do hotela](https://m.example)` | text `"Pot: "`, external(`Pot do       |
 *   |                                           | hotela` → `https://m.example`)         |
 *   | `[Grad Ptuj – pot](https://a.example)`    | one external, the label whole —        |
 *   |                                           | diacritics and dash included           |
 *   | `[Pot](https://x.example/?a=1&b=%C5%A1)`  | one external; `&` and `%C5%A1` are     |
 *   |                                           | address characters and end nothing     |
 *   | `[Hotel](https://en.wikipedia.org/wiki/`  | one external, the inner `()` kept —    |
 *   | `Hotel_(disambiguation))`                 | the LAST `)` closes the Markdown link  |
 *   | `[Pot](https://a.example) in https://b.`  | external, text `" in "`, external —    |
 *   | `example`                                 | the bare address is split as always    |
 *   | `[Soba 104](/rooms/104)`                  | all text: no prefix is listed here, so |
 *   |                                           | the target is not a target at all      |
 *   | `[Pot](http://a.example)`                 | NOT a Markdown link — the target must  |
 *   |                                           | be `https://` — so the bare scanner    |
 *   |                                           | takes the `http://` address as it      |
 *   |                                           | always has: text `"[Pot]("`, external, |
 *   |                                           | text `")"`                             |
 *   | `[Pot](https://a.example`                 | unclosed, so not a Markdown link: text |
 *   |                                           | `"[Pot]("`, then the bare address      |
 *   | `Nekaj [ tukaj in [Pot](https://a.`       | text `"Nekaj [ tukaj in "`, then       |
 *   | `example)`                                | external(`Pot` → …) — a stray `[`      |
 *   |                                           | loses to the second one                |
 */

/** What one piece of a split line is. */
export type LinkKind = 'text' | 'external' | 'mail' | 'internal'

export interface ILinkSegment {
    kind: LinkKind
    /** What is drawn. A long external address is shortened; everything else is whole. */
    text: string
    /** Where it goes. Empty on a `text` segment. */
    url: string
}

/**
 * The first path segment an internal link may start with — EMPTY in this portal, and
 * that is the whole of the decision.
 *
 * The reception portal lists `reservations`, `rezervacije`, `folios`, `folio`,
 * `skupine`, `staff`, because those are its own screens and the model does now and then
 * name one. Not one of them exists here: this portal has a round, a sheet, a board and
 * a task, and nothing else.
 *
 * The tempting move is to list THIS portal's prefixes instead, and it is the wrong one.
 * Nothing tells the model that the housekeeping portal exists — the system prompt names
 * no portal route at all (`Logic/Chat.go`), so every path it writes is a reception one
 * written from its own idea of the address book. Listing `rooms` here would not catch
 * any of those; what it would catch is `/rooms/104`, which the model can plausibly
 * write about a ROOM NUMBER while `/rooms/:id` in this portal takes a TASK id. That is
 * a link to a broken screen made out of a coincidence, which is worse than the plain
 * text a reader can act on.
 *
 * So: reception paths fall through as ordinary text (they match no prefix), and this
 * portal's own paths are never offered. `AssistantText.vue` still asks the router
 * before drawing an internal segment; it is simply never given one.
 */
const INTERNAL_PREFIXES: string[] = []

/**
 * How long an external address may be before it is shortened, measured after the
 * scheme has been taken off. Thirty-six characters is about a line of a chat bubble at
 * 440 px, which is the width the drawer gives this.
 */
export const MAX_LINK_LABEL = 36

/** A local part conservative enough that Slovenian prose cannot walk into it. */
const EMAIL = String.raw`[A-Za-z0-9._%+-]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)+`

/**
 * What a Markdown link may point AT: an `https://` address, or — in the portal that has
 * prefixes — a path into it.
 *
 * `http://` is left out on purpose. A Markdown link is written by the model rather than
 * copied from the hotel's own data, and the shape it writes for a real address is the
 * secure one; accepting the other would be inventing a plain-text link on the model's
 * behalf. A bare `http://` address in the same answer is still split as it always was —
 * that behaviour is not this branch's to change.
 *
 * An `https://` address may carry a bracket PAIR of its own, `…/Hotel_(disambiguation)`,
 * while a lone `)` ends it. That is `trimTrailing`'s rule written as a pattern, because
 * here the `)` that ends the link is a character the syntax owns rather than one the
 * sentence lent it.
 *
 * The internal branch is dropped when there are no prefixes, for the reason `PATTERN`
 * gives below: an empty alternation matches a bare slash.
 */
const MARKDOWN_TARGET = [
    String.raw`https:\/\/(?:[^\s()<>"']|\([^\s()<>"']*\))+`,
    ...(INTERNAL_PREFIXES.length
        ? [String.raw`\/(?:${INTERNAL_PREFIXES.join('|')})(?:\/[^\s()<>"']*)?`]
        : []),
].join('|')

/**
 * A Markdown link, `[label](target)`.
 *
 * The label is FENCED by three things and not one: it holds no `]`, which is what ends
 * it; no `[` either; and no line break, in at most a hundred and twenty characters.
 *
 * Barring `[` is the half that earns its place. Without it a stray bracket earlier in a
 * sentence reaches forward to the first `](https://` it can find and swallows the prose
 * in between — "Nekaj [ tukaj in [Pot](https://…)" drew ONE link labelled " tukaj in
 * [Pot". With it, the link starts at the second bracket and the prose stays prose. The
 * length is the backstop for the line that has no stray bracket at all: a label is a
 * phrase somebody reads in one bubble, and a longer one is a paragraph the model did
 * not mean as a label.
 *
 * Its two groups are the ONLY capturing groups in `PATTERN`; the loop reads them to tell
 * a Markdown match from a bare one.
 */
const MARKDOWN = String.raw`\[([^\]\[\r\n]{1,120})\]\((${MARKDOWN_TARGET})\)`

/** Ordered: a URL that starts earlier swallows an address that sits inside it. A
 *  Markdown link needs no place in that order — it starts at its `[`, which is earlier
 *  in the line than anything inside it, and `exec` takes the earliest match.
 *
 *  The internal branch is dropped altogether when there are no prefixes, rather than
 *  built from an empty alternation: `\/(?:)` matches a bare slash, which would turn
 *  every "1/2" in an answer into a link to the front page. */
const PATTERN = [
    MARKDOWN,
    String.raw`https?:\/\/[^\s<>"']+`,
    String.raw`mailto:[^\s<>"']+`,
    EMAIL,
    ...(INTERNAL_PREFIXES.length
        ? [String.raw`\/(?:${INTERNAL_PREFIXES.join('|')})(?:\/[^\s<>"']*)?`]
        : []),
].join('|')

const SENTENCE_END = /[.,;:]+$/

/**
 * Takes the sentence's own punctuation back off the end of a token.
 *
 * A closing bracket is the one that cannot be stripped blindly: it ends a sentence far
 * more often than it ends an address, but an address that opened a bracket itself owns
 * the one that closes it — `…/Hotel_(disambiguation)` is a real link and `(https://x)`
 * is a real aside.
 */
const trimTrailing = (value: string): string => {
    let out = value.replace(SENTENCE_END, '')

    while (out.endsWith(')') && !out.includes('(')) out = out.slice(0, -1).replace(SENTENCE_END, '')

    return out
}

/**
 * An external address as it is READ: no scheme, no `www.`, no trailing slash, and cut
 * with an ellipsis when it is longer than a bubble. What it POINTS at is untouched —
 * the two are separate fields precisely so that shortening can never shorten a link.
 */
export const shortenUrl = (url: string): string => {
    const bare = url
        .replace(/^https?:\/\//i, '')
        .replace(/^www\./i, '')
        .replace(/\/$/, '')

    return bare.length > MAX_LINK_LABEL ? `${bare.slice(0, MAX_LINK_LABEL - 1)}…` : bare
}

/** A token's kind, decided by how it starts. Anything left is a bare e-mail address. */
const kindOf = (token: string): LinkKind => {
    if (/^https?:\/\//i.test(token)) return 'external'
    if (/^mailto:/i.test(token)) return 'mail'
    if (token.startsWith('/')) return 'internal'

    return 'mail'
}

/** Builds the segment for one token, or nothing when what is left of it is not a link. */
const segmentFor = (token: string): ILinkSegment | null => {
    const kind = kindOf(token)

    if (kind === 'external') {
        const label = shortenUrl(token)

        // "https://" with the address trimmed off it is not an address.
        return label ? { kind, text: label, url: token } : null
    }

    if (kind === 'internal') return token.length > 1 ? { kind, text: token, url: token } : null

    const address = token.replace(/^mailto:/i, '')

    return address.includes('@') ? { kind: 'mail', text: address, url: `mailto:${address}` } : null
}

/**
 * The line, split.
 *
 * Always returns at least one segment for a non-empty line, and joining every
 * segment's source back together reproduces the input exactly — nothing is dropped and
 * nothing is invented, which is what makes this safe to draw without escaping worries.
 */
export const linkify = (text: string): ILinkSegment[] => {
    const segments: ILinkSegment[] = []

    if (!text) return segments

    // Built here rather than once at module scope: a /g regex carries `lastIndex`
    // between calls, and a shared one would start the second line where it left the
    // first.
    const pattern = new RegExp(PATTERN, 'g')

    let last = 0
    let match: RegExpExecArray | null

    while ((match = pattern.exec(text)) !== null) {
        const whole = match[0]
        const [, markdownLabel, markdownTarget] = match

        // A Markdown link, consumed whole: the source span is the entire `[…](…)` and
        // exactly one segment comes out of it, of whichever kind the target is. Nothing
        // is trimmed off the end — the `)` there belongs to the link's own syntax, not
        // to the sentence around it — and the label is drawn as the model wrote it.
        if (markdownLabel !== undefined && markdownTarget !== undefined) {
            if (match.index > last) segments.push({ kind: 'text', text: text.slice(last, match.index), url: '' })

            segments.push({ kind: kindOf(markdownTarget), text: markdownLabel, url: markdownTarget })

            last = match.index + whole.length
            pattern.lastIndex = last

            continue
        }

        const token = trimTrailing(whole)
        const before = match.index > 0 ? text[match.index - 1] : ''

        // A path is a path at the start of a line or after a space, never glued to the
        // character before it: `12/staff/3` is a note about a shift, not a link.
        const glued = token.startsWith('/') && /[\w/@.-]/.test(before)

        const segment = glued ? null : segmentFor(token)

        if (!segment) {
            // Left where it is; it becomes part of the next text segment.
            pattern.lastIndex = match.index + whole.length

            continue
        }

        if (match.index > last) segments.push({ kind: 'text', text: text.slice(last, match.index), url: '' })

        segments.push(segment)

        last = match.index + token.length
        pattern.lastIndex = last
    }

    if (last < text.length) segments.push({ kind: 'text', text: text.slice(last), url: '' })

    return segments
}
